import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Copy, Edit, Plus, RefreshCw, Printer } from 'lucide-react';
import { useAuth } from '../../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../../contexts/I18nProvider';
import { itemsService } from '../../../../../../services/itemsService';
import { stcBinGrpService } from '../../../../../../services/stcBinGrpService';
import reportConfigurationService from '../../../../../../services/reportConfigurationService';
import { stcMovementTypeService } from '../../../../../../services/stcMovementTypeService';
import { stcSortingBoxDefService } from '../../../../../../services/stcSortingBoxDefService';
import { itemBinSizeGroupService } from '../../../../../../services/itemBinSizeGroupService';
import { statusCodesService } from '../../../../../../services/statusCodesService';
import { ItemsDto, ItemsPagedDto, ItemsPagedQueryParams, UpdateItemsRequest } from '../../../../../../types/items';
import { MasterDataLookups } from './types';
import { ItemsBulkUpdateModal } from './ItemsBulkUpdateModal';
import { ItemsCopyPropertiesModal } from './ItemsCopyPropertiesModal';
import { LabelPrintModal } from '../../../../../common/LabelPrintModal';
import { WarehouseTable, useInlineEdit } from '../../../../../shared/table';
import { createColumnFactory } from '../../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction, ServerFetchParams } from '../../../../../shared/table/types/table';

// StatusType-konstantar for oppslag mot StatusCodes-tabellen
const STATUS_TYPE_PICK_RULE = 24;   // Plukkregel (FIFO, Antall, Prioritet, etc.)
const STATUS_TYPE_ITEM_TYPE = 32;   // Varetype (Standard, Batchnr, Serienummer, etc.)

export const ItemsDataTable: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const companyNo = user?.companyNo || '';
  // Dyplenke fra varekortet: ?module=warehouse-maintenance&sub=items&itemNo=X filtrerer på varenr.
  const [initialItemNoFilter] = useState(() => new URLSearchParams(window.location.search).get('itemNo')?.trim() || '');

  // Server-side pagination state
  const [serverItems, setServerItems] = useState<ItemsPagedDto[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [masterData, setMasterData] = useState<MasterDataLookups | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [selectedItemNos, setSelectedItemNos] = useState<string[]>([]);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showCopyPropsModal, setShowCopyPropsModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);

  // Track whether initial load is done (to avoid full-screen spinner on filter changes)
  const initialLoadDoneRef = useRef(false);
  // Monotonisk teller — forkaster utdaterte svar (siste fetch vinner) i stedet for å droppe
  // overlappende fetch. Viktig fordi dropdown-valg kan fyre tett (rask klikking).
  const fetchIdRef = useRef(0);
  // Store latest fetch params for refresh after edit/delete
  const latestFetchParamsRef = useRef<ItemsPagedQueryParams>({ pageNumber: 1, pageSize: 50 });
  // Debounce timer for column filters
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Create the column factory
  const columnFactory = createColumnFactory<ItemsPagedDto>();

  // Fetch paged items from server
  const fetchPagedItems = useCallback(async (params: ItemsPagedQueryParams) => {
    if (!companyNo) return;

    // Only show full loading spinner on initial load — subsequent fetches keep table visible (B1)
    if (!initialLoadDoneRef.current) {
      setIsLoading(true);
    }
    setError(null);

    // Request-ID; nyere fetch som starter før denne er ferdig forkaster dette svaret (siste vinner)
    const thisRequestId = ++fetchIdRef.current;
    try {
      const result = await itemsService.getCurrentItemsPaged(params);
      if (thisRequestId !== fetchIdRef.current) return; // utdatert svar — forkast

      setServerItems(result.items);
      setTotalRecords(result.totalRecords);
      latestFetchParamsRef.current = params;
      initialLoadDoneRef.current = true;
    } catch (err) {
      console.error('Error loading paged items:', err);
      if (thisRequestId === fetchIdRef.current) {
        setError(t('manager.modules.adminItemsLocations.items.errorLoad', {}, 'Kunne ikke laste varedata. Prøv igjen.'));
      }
    } finally {
      if (thisRequestId === fetchIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [companyNo, t]);

  // Load master data (for dropdown edit options) — only once on mount
  const loadMasterData = useCallback(async () => {
    if (!companyNo) return;
    try {
      const [binGroupsData, reportsData, movementTypesData, sortingBoxesData, itemBinSizeGroupsData, pickRuleCodes, itemTypeCodes] = await Promise.all([
        stcBinGrpService.getCurrent(),
        reportConfigurationService.getAll(companyNo),
        stcMovementTypeService.getCurrent(),
        stcSortingBoxDefService.getCurrent(),
        itemBinSizeGroupService.getCurrent().catch(() => []),
        statusCodesService.getByStatusType(STATUS_TYPE_PICK_RULE),
        statusCodesService.getByStatusType(STATUS_TYPE_ITEM_TYPE)
      ]);

      // Bygg Maps fra StatusCodes-oppslag (erstatter tidlegare hardkoda verdiar)
      const pickRulesMap = new Map(
        pickRuleCodes.map(sc => [sc.statusCode, { pickRuleNo: sc.statusCode, description: sc.lng_1 }])
      );
      const itemTypesMap = new Map(
        itemTypeCodes.map(sc => [sc.statusCode, { itemType: sc.statusCode, description: sc.lng_1 }])
      );

      const lookups: MasterDataLookups = {
        binGrps: new Map(binGroupsData.map(g => [g.binGrpNo, g])),
        reports: new Map(reportsData.map(r => [r.reportID, r])),
        movementTypes: new Map(movementTypesData.map(m => [m.movementTypeNo, m])),
        sortingBoxes: new Map(sortingBoxesData.map(s => [s.stcBoxTypeID, s])),
        pickRules: pickRulesMap,
        itemTypes: itemTypesMap,
        itemBinSizeGroups: new Map(itemBinSizeGroupsData.map(g => [g.itemBinSizeGroupID, g]))
      };

      setMasterData(lookups);
    } catch (err) {
      console.error('Error loading master data:', err);
    }
  }, [companyNo]);

  // Server-side pagination callback — called by WarehouseTable when page/sort/filter changes
  const handleServerFetch = useCallback((params: ServerFetchParams) => {
    const f = params.filters;
    const d = params.dropdownFilters || {};

    // B3: flervalgs lookup-filter — dropdown sender koder komma-separert (IN), overstyrer fritekst.
    const joinCodes = (arr?: string[]) => (arr && arr.length > 0 ? arr.join(',') : undefined);
    const binGrpNos = joinCodes(d.binGrpNo);
    const itemBinSizeGroupIDs = joinCodes(d.itemBinSizeGroupID);
    const itemTypes = joinCodes(d.itemType);
    const pickRuleNos = joinCodes(d.pickRuleNo);
    const movementTypeNos = joinCodes(d.movementTypeNo);
    const reportIDs = joinCodes(d.reportID);
    const stcBoxTypeIDs = joinCodes(d.stcBoxTypeID);

    const queryParams: ItemsPagedQueryParams = {
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
      sortColumn: params.sortColumn,
      sortDirection: params.sortDirection,
      // Text columns
      itemNo: f?.itemNo || undefined,
      description1: f?.description1 || undefined,
      itemBarCode: f?.itemBarCode || undefined,
      supplierItemNo: f?.supplierItemNo || undefined,
      stdLocationName: f?.stdLocationName || undefined,
      stdBinCode: f?.stdBinCode || undefined,
      itemGrp1: f?.itemGrp1 || undefined,
      itemGrp2: f?.itemGrp2 || undefined,
      itemGrp3: f?.itemGrp3 || undefined,
      itemPictureName: f?.itemPictureName || undefined,
      currency: f?.currency || undefined,
      itemUnitCode: f?.itemUnitCode || undefined,
      itemUnitTxt: f?.itemUnitTxt || undefined,
      appFreeField1: f?.appFreeField1 || undefined,
      appFreeField2: f?.appFreeField2 || undefined,
      internalFree1: f?.internalFree1 || undefined,
      internalFree2: f?.internalFree2 || undefined,
      internalFree3: f?.internalFree3 || undefined,
      internalFree4: f?.internalFree4 || undefined,
      updatedBy: f?.updatedBy || undefined,
      createdBy: f?.createdBy || undefined,
      freeTxt1: f?.freeTxt1 || undefined,
      freeTxt2: f?.freeTxt2 || undefined,
      freeTxt3: f?.freeTxt3 || undefined,
      freeTxt4: f?.freeTxt4 || undefined,
      freeTxt5: f?.freeTxt5 || undefined,
      freeTxt6: f?.freeTxt6 || undefined,
      freeTxt7: f?.freeTxt7 || undefined,
      freeTxt8: f?.freeTxt8 || undefined,
      freeTxt9: f?.freeTxt9 || undefined,
      freeTxt10: f?.freeTxt10 || undefined,
      // Lookup-felt: dropdown (IN) overstyrer fritekst
      binGrpNo: binGrpNos ? undefined : (f?.binGrpNo || undefined),
      itemBinSizeGroupID: itemBinSizeGroupIDs ? undefined : (f?.itemBinSizeGroupID || undefined),
      itemType: itemTypes ? undefined : (f?.itemType || undefined),
      pickRuleNo: pickRuleNos ? undefined : (f?.pickRuleNo || undefined),
      movementTypeNo: movementTypeNos ? undefined : (f?.movementTypeNo || undefined),
      reportID: reportIDs ? undefined : (f?.reportID || undefined),
      stcBoxTypeID: stcBoxTypeIDs ? undefined : (f?.stcBoxTypeID || undefined),
      binGrpNos, itemBinSizeGroupIDs, itemTypes, pickRuleNos,
      movementTypeNos, reportIDs, stcBoxTypeIDs,
      maxReplenishQty: f?.maxReplenishQty || undefined,
      // Decimal columns
      quantity: f?.quantity || undefined,
      quantityReserved: f?.quantityReserved || undefined,
      quantityAvailable: f?.quantityAvailable || undefined,
      purchasePrice: f?.purchasePrice || undefined,
      unitFactor: f?.unitFactor || undefined,
      // Date columns
      lastUpdate: f?.lastUpdate || undefined,
      created: f?.created || undefined,
      // Boolean columns
      putAwayToPickableBinYesNo: f?.putAwayToPickableBinYesNo || undefined,
      stcTransactionsYesNo: f?.stcTransactionsYesNo || undefined,
      newLineYesNo: f?.newLineYesNo || undefined,
      useSharedBinYesNo: f?.useSharedBinYesNo || undefined,
      useMaxReplenishQtyYesNo: f?.useMaxReplenishQtyYesNo || undefined,
    };

    // Debounce filter changes (300ms) but execute page/sort changes immediately
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
    }

    // Detect if any filter value changed (compared to previous fetch)
    const prevFilters = latestFetchParamsRef.current;
    const filterKeys: (keyof ItemsPagedQueryParams)[] = [
      'itemNo', 'description1', 'itemBarCode', 'supplierItemNo',
      'stdLocationName', 'stdBinCode', 'itemGrp1', 'itemGrp2', 'itemGrp3',
      'itemPictureName', 'currency', 'itemUnitCode', 'itemUnitTxt',
      'appFreeField1', 'appFreeField2',
      'internalFree1', 'internalFree2', 'internalFree3', 'internalFree4',
      'updatedBy', 'createdBy',
      'freeTxt1', 'freeTxt2', 'freeTxt3', 'freeTxt4', 'freeTxt5',
      'freeTxt6', 'freeTxt7', 'freeTxt8', 'freeTxt9', 'freeTxt10',
      'binGrpNo', 'itemBinSizeGroupID', 'itemType', 'pickRuleNo', 'movementTypeNo',
      'reportID', 'stcBoxTypeID', 'maxReplenishQty',
      'quantity', 'quantityReserved', 'quantityAvailable', 'purchasePrice', 'unitFactor',
      'lastUpdate', 'created',
      'putAwayToPickableBinYesNo', 'stcTransactionsYesNo', 'newLineYesNo',
      'useSharedBinYesNo', 'useMaxReplenishQtyYesNo',
      // B3: flervalgs lookup-filter (IN)
      'binGrpNos', 'itemBinSizeGroupIDs', 'itemTypes', 'pickRuleNos',
      'movementTypeNos', 'reportIDs', 'stcBoxTypeIDs',
    ];
    const isFilterChange = params.pageNumber === 1 &&
      filterKeys.some(key => queryParams[key] !== prevFilters[key]);

    if (isFilterChange) {
      filterDebounceRef.current = setTimeout(() => {
        fetchPagedItems(queryParams);
      }, 300);
    } else {
      fetchPagedItems(queryParams);
    }
  }, [fetchPagedItems]);

  // Refresh helper — re-fetches with the latest params
  const refreshData = useCallback(() => {
    fetchPagedItems(latestFetchParamsRef.current);
  }, [fetchPagedItems]);

  // Initial load
  useEffect(() => {
    fetchPagedItems({ pageNumber: 1, pageSize: 50, itemNo: initialItemNoFilter || undefined });
    loadMasterData();
  }, [fetchPagedItems, loadMasterData, initialItemNoFilter]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current);
      }
    };
  }, []);

  // Inline editing
  const inlineEditConfig = useInlineEdit<ItemsPagedDto>({
    getRowId: (row) => row.itemNo,
    onSave: async (_originalRow, editedData) => {
      const request: UpdateItemsRequest = {
        description1: editedData.description1,
        itemBarCode: editedData.itemBarCode,
        supplierItemNo: editedData.supplierItemNo,
        itemUnitCode: editedData.itemUnitCode,
        itemUnitTxt: editedData.itemUnitTxt,
        purchasePrice: editedData.purchasePrice,
        currency: editedData.currency,
        unitFactor: editedData.unitFactor,
        binGrpNo: editedData.binGrpNo,
        pickRuleNo: editedData.pickRuleNo,
        stcBoxTypeID: editedData.stcBoxTypeID,
        itemBinSizeGroupID: editedData.itemBinSizeGroupID,
        itemType: editedData.itemType,
        putAwayToPickableBinYesNo: editedData.putAwayToPickableBinYesNo,
        movementTypeNo: editedData.movementTypeNo,
        stcTransactionsYesNo: editedData.stcTransactionsYesNo,
        newLineYesNo: editedData.newLineYesNo,
        itemGrp1: editedData.itemGrp1,
        itemGrp2: editedData.itemGrp2,
        itemGrp3: editedData.itemGrp3,
        freeTxt1: editedData.freeTxt1,
        freeTxt2: editedData.freeTxt2,
        freeTxt3: editedData.freeTxt3,
        freeTxt4: editedData.freeTxt4,
        freeTxt5: editedData.freeTxt5,
        freeTxt6: editedData.freeTxt6,
        freeTxt7: editedData.freeTxt7,
        freeTxt8: editedData.freeTxt8,
        freeTxt9: editedData.freeTxt9,
        freeTxt10: editedData.freeTxt10,
        appFreeField1: editedData.appFreeField1,
        appFreeField2: editedData.appFreeField2,
        internalFree1: editedData.internalFree1,
        internalFree2: editedData.internalFree2,
        internalFree3: editedData.internalFree3,
        internalFree4: editedData.internalFree4,
        itemPictureName: editedData.itemPictureName,
        // Tolldata (TW 36395424) — normaliseres i backend (HS: kun sifre, land: ISO2)
        hsCode: editedData.hsCode || null,
        countryOfOrigin: editedData.countryOfOrigin || null,
        grossWeight: editedData.grossWeight === undefined || editedData.grossWeight === null || (editedData.grossWeight as unknown) === ''
          ? null
          : Number(editedData.grossWeight),
        stdLocationName: editedData.stdLocationName,
        reportID: editedData.reportID,
        stdBinCode: editedData.stdBinCode,
        useSharedBinYesNo: editedData.useSharedBinYesNo,
        useMaxReplenishQtyYesNo: editedData.useMaxReplenishQtyYesNo,
        maxReplenishQty: editedData.maxReplenishQty,
        // Tri-state (TW 36344429): select-editoren gir string ('true'/'false'/'' = arv),
        // uendret verdi er boolean/null fra raden. null = arv fra profil.
        barCodeConfirmItem: typeof editedData.barCodeConfirmItem === 'string'
          ? (editedData.barCodeConfirmItem === 'true' ? true
            : editedData.barCodeConfirmItem === 'false' ? false
            : null)
          : editedData.barCodeConfirmItem
      };
      await itemsService.updateItem(companyNo, _originalRow.itemNo, request);
    },
    onSaveSuccess: () => refreshData(),
    onSaveError: (err) => {
      setSaveError(err.message || t('manager.modules.adminItemsLocations.items.errorSave', {}, 'Feil ved lagring'));
      setTimeout(() => setSaveError(null), 8000);
    }
  });

  // Build dropdown options from master data for inline editing
  const binGrpOptions = useMemo(() =>
    masterData ? Array.from(masterData.binGrps.values()).map(g => ({
      value: g.binGrpNo, label: `${g.binGrpNo} - ${g.description1}`
    })) : [], [masterData]);

  const pickRuleOptions = useMemo(() =>
    masterData ? Array.from(masterData.pickRules.values()).map(r => ({
      value: r.pickRuleNo, label: `${r.pickRuleNo} - ${r.description}`
    })) : [], [masterData]);

  const sortingBoxOptions = useMemo(() =>
    masterData ? Array.from(masterData.sortingBoxes.values()).map(s => ({
      value: s.stcBoxTypeID, label: `${s.stcBoxTypeID} - ${s.description1}`
    })) : [], [masterData]);

  const movementTypeOptions = useMemo(() =>
    masterData ? Array.from(masterData.movementTypes.values()).map(m => ({
      value: m.movementTypeNo, label: `${m.movementTypeNo} - ${m.description1}`
    })) : [], [masterData]);

  const itemBinSizeGroupOptions = useMemo(() =>
    masterData ? Array.from(masterData.itemBinSizeGroups.values()).map(g => ({
      value: g.itemBinSizeGroupID, label: `${g.itemBinSizeGroupID} - ${g.description1}`
    })) : [], [masterData]);

  const reportOptions = useMemo(() =>
    masterData ? Array.from(masterData.reports.values()).map(r => ({
      value: r.reportID, label: r.reportName
    })) : [], [masterData]);

  const itemTypeOptions = useMemo(() =>
    masterData ? Array.from(masterData.itemTypes.values()).map(t => ({
      value: t.itemType, label: `${t.itemType} - ${t.description}`
    })) : [], [masterData]);

  // B3: valglister for flervalgs lookup-filter (kombinert syntetisk datasett — ColumnDropdownFilter
  // leser item[column.key] per kolonne og hopper over null/undefined). Hver rad bærer kode + den
  // beskrivelsen som kolonnens formatter leser (server-enriched feltnavn på ItemsPagedDto).
  const dropdownOptionsData = useMemo<ItemsPagedDto[]>(() => {
    if (!masterData) return [];
    const rows: ItemsPagedDto[] = [];
    masterData.itemTypes.forEach(it => rows.push({ itemType: it.itemType } as unknown as ItemsPagedDto));
    masterData.pickRules.forEach(pr => rows.push({ pickRuleNo: pr.pickRuleNo } as unknown as ItemsPagedDto));
    masterData.binGrps.forEach(g => rows.push({ binGrpNo: g.binGrpNo, binGrpDescription: g.description1 } as unknown as ItemsPagedDto));
    masterData.itemBinSizeGroups.forEach(g => rows.push({ itemBinSizeGroupID: g.itemBinSizeGroupID, itemBinSizeGroupDescription: g.description1 } as unknown as ItemsPagedDto));
    masterData.movementTypes.forEach(m => rows.push({ movementTypeNo: m.movementTypeNo, movementTypeDescription: m.description1 } as unknown as ItemsPagedDto));
    masterData.reports.forEach(r => rows.push({ reportID: r.reportID, reportName: r.reportName } as unknown as ItemsPagedDto));
    masterData.sortingBoxes.forEach(s => rows.push({ stcBoxTypeID: s.stcBoxTypeID, stcBoxTypeDescription: s.description1 } as unknown as ItemsPagedDto));
    return rows;
  }, [masterData]);

  // Handle bulk edit button
  const handleBulkEdit = useCallback(() => {
    if (selectedItemNos.length === 0) {
      alert(t('manager.modules.adminItemsLocations.items.selectAtLeastOne', {}, 'Velg minst én vare for å redigere'));
      return;
    }
    setShowBulkEditModal(true);
  }, [selectedItemNos.length, t]);

  // Handle modal update success
  const handleUpdateSuccess = () => {
    refreshData();
    setSelectedItemNos([]);
    setShowBulkEditModal(false);
  };

  const handleCopyPropsSuccess = () => {
    refreshData();
    setSelectedItemNos([]);
    setShowCopyPropsModal(false);
  };

  // Handle modal delete success
  const handleDeleteSuccess = () => {
    refreshData();
    setSelectedItemNos([]);
    setShowBulkEditModal(false);
  };

  // Custom row ID function
  const getRowId = useCallback((item: ItemsPagedDto) => item.itemNo, []);

  // Handle selection change
  const handleSelectionChange = useCallback((selectedIds: (string | number)[], _selectedData: ItemsPagedDto[]) => {
    setSelectedItemNos(selectedIds.map(id => String(id)));
  }, []);

  // Column definitions using the factory
  const columns: ColumnDefinition<ItemsPagedDto>[] = useMemo(() => [
    columnFactory.createTextColumn('itemNo', 'itemNo', t('warehouse.general.itemNumber', {}, 'Varenr.'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text'
    }),

    columnFactory.createTextColumn('description1', 'description1', t('common.fields.description', {}, 'Beskrivelse'), {
      minWidth: 200,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createNumberColumn('itemType', 'itemType', t('manager.modules.adminItemsLocations.items.colItemType', {}, 'Varetype'), {
      minWidth: 180,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value: any) => {
        if (value === undefined || value === null) return '-';
        const typeInfo = masterData?.itemTypes.get(value);
        return typeInfo ? `${value} - ${typeInfo.description}` : String(value);
      },
      editable: true,
      editType: 'select',
      editOptions: itemTypeOptions
    }),

    columnFactory.createTextColumn('itemBarCode', 'itemBarCode', t('manager.modules.adminItemsLocations.items.colGtin', {}, 'GTIN'), {
      minWidth: 150,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('supplierItemNo', 'supplierItemNo', t('manager.modules.adminItemsLocations.items.colSupplierItemNo', {}, 'Levr. varenr.'), {
      minWidth: 150,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('itemUnitCode', 'itemUnitCode', t('manager.modules.adminItemsLocations.items.colUnitCode', {}, 'Enhetskode'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('itemUnitTxt', 'itemUnitTxt', t('manager.modules.adminItemsLocations.items.colUnitText', {}, 'Enhetstekst'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createNumberColumn('quantity', 'quantity', t('common.fields.quantity', {}, 'Antall'), {
      minWidth: 100,
      sortable: true,
      filterType: 'text'
    }),

    columnFactory.createNumberColumn('quantityReserved', 'quantityReserved', t('warehouse.general.reserved', {}, 'Reservert'), {
      minWidth: 100,
      sortable: true,
      filterType: 'text'
    }),

    columnFactory.createNumberColumn('quantityAvailable', 'quantityAvailable', t('warehouse.general.available', {}, 'Tilgjengelig'), {
      minWidth: 110,
      sortable: true,
      filterType: 'text'
    }),

    columnFactory.createNumberColumn('purchasePrice', 'purchasePrice', t('manager.modules.adminItemsLocations.items.colPurchasePrice', {}, 'Innkjøpspris'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('currency', 'currency', t('manager.modules.adminItemsLocations.items.colCurrency', {}, 'Valuta'), {
      minWidth: 80,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    // Tolldata (TW 36395424) — NVIT-deklarasjon til TA. Ingen kolonnefilter (ikke i backend-filtrene).
    columnFactory.createTextColumn('hsCode', 'hsCode', t('manager.modules.adminItemsLocations.items.colHsCode', {}, 'HS-kode'), {
      minWidth: 110,
      sortable: true,
      editable: true
    }),

    columnFactory.createTextColumn('countryOfOrigin', 'countryOfOrigin', t('manager.modules.adminItemsLocations.items.colCountryOfOrigin', {}, 'Opprinnelsesland'), {
      minWidth: 110,
      sortable: true,
      editable: true
    }),

    columnFactory.createNumberColumn('grossWeight', 'grossWeight', t('manager.modules.adminItemsLocations.items.colGrossWeight', {}, 'Bruttovekt (kg)'), {
      minWidth: 120,
      sortable: true,
      editable: true
    }),

    columnFactory.createNumberColumn('unitFactor', 'unitFactor', t('manager.modules.adminItemsLocations.items.colUnitFactor', {}, 'Enhetsfaktor'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createNumberColumn('binGrpNo', 'binGrpNo', t('manager.modules.adminItemsLocations.items.colBinGrp', {}, 'Lokasjonsgruppe'), {
      minWidth: 180,
      sortable: true,
      filterType: 'dropdown',
      alignment: 'left',
      formatter: (value: any, item: ItemsPagedDto) => item.binGrpDescription ? `${value} - ${item.binGrpDescription}` : String(value || '-'),
      editable: true,
      editType: 'select',
      editOptions: binGrpOptions
    }),

    columnFactory.createNumberColumn('pickRuleNo', 'pickRuleNo', t('manager.modules.adminItemsLocations.items.colPickRule', {}, 'Plukkregel'), {
      minWidth: 150,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value: any, item: ItemsPagedDto) => {
        if (!value && value !== 0) return '-';
        const ruleInfo = masterData?.pickRules.get(value);
        return ruleInfo ? `${value} - ${ruleInfo.description}` : String(value);
      },
      editable: true,
      editType: 'select',
      editOptions: pickRuleOptions
    }),

    columnFactory.createTextColumn('stcBoxTypeID', 'stcBoxTypeID', t('manager.modules.adminItemsLocations.items.colSortingBox', {}, 'Sorteringskasse'), {
      minWidth: 150,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value: any, item: ItemsPagedDto) => item.stcBoxTypeDescription ? `${value} - ${item.stcBoxTypeDescription}` : String(value || '-'),
      editable: true,
      editType: 'select',
      editOptions: sortingBoxOptions
    }),

    columnFactory.createNumberColumn('itemBinSizeGroupID', 'itemBinSizeGroupID', t('manager.modules.adminItemsLocations.items.colSizeGroup', {}, 'Størrelsegruppe'), {
      minWidth: 180,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value: any, item: ItemsPagedDto) => item.itemBinSizeGroupDescription ? `${value} - ${item.itemBinSizeGroupDescription}` : String(value || '-'),
      editable: true,
      editType: 'select',
      editOptions: itemBinSizeGroupOptions
    }),

    columnFactory.createBooleanColumn('putAwayToPickableBinYesNo', 'putAwayToPickableBinYesNo', t('manager.modules.adminItemsLocations.items.colReceiveToPick', {}, 'Mottak til plukklok.'), {
      minWidth: 160,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createNumberColumn('movementTypeNo', 'movementTypeNo', t('manager.modules.adminItemsLocations.items.colMovementType', {}, 'Bevegelseskode'), {
      minWidth: 150,
      sortable: true,
      filterType: 'dropdown',
      alignment: 'left',
      formatter: (value: any, item: ItemsPagedDto) => item.movementTypeDescription ? `${value} - ${item.movementTypeDescription}` : String(value || '-'),
      editable: true,
      editType: 'select',
      editOptions: movementTypeOptions
    }),

    columnFactory.createBooleanColumn('stcTransactionsYesNo', 'stcTransactionsYesNo', t('manager.modules.adminItemsLocations.items.colStockControl', {}, 'Lagerstyring'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createBooleanColumn('newLineYesNo', 'newLineYesNo', t('manager.modules.adminItemsLocations.items.colNewLine', {}, 'Tilleggsvare'), {
      minWidth: 100,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    // Unntaksflagg vareskann (TW 36344429): tri-state — Arv (NULL) / Ja / Nei
    columnFactory.createTextColumn('barCodeConfirmItem', 'barCodeConfirmItem', t('manager.modules.adminItemsLocations.items.colBarCodeConfirmItem', {}, 'Krev vareskann'), {
      minWidth: 160,
      sortable: true,
      filterType: 'none',
      formatter: (value: any) => value === true
        ? t('manager.modules.adminItemsLocations.items.scanFlagYes', {}, 'Ja')
        : value === false
          ? t('manager.modules.adminItemsLocations.items.scanFlagNo', {}, 'Nei')
          : t('manager.modules.adminItemsLocations.items.scanFlagInherit', {}, 'Arv (fra profil)'),
      editable: true,
      editType: 'select',
      editOptions: [
        { value: '', label: t('manager.modules.adminItemsLocations.items.scanFlagInherit', {}, 'Arv (fra profil)') },
        { value: 'true', label: t('manager.modules.adminItemsLocations.items.scanFlagYes', {}, 'Ja') },
        { value: 'false', label: t('manager.modules.adminItemsLocations.items.scanFlagNo', {}, 'Nei') }
      ]
    }),

    columnFactory.createTextColumn('itemGrp1', 'itemGrp1', t('manager.modules.adminItemsLocations.items.colItemGrp1', {}, 'VareGrp. 1'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('itemGrp2', 'itemGrp2', t('manager.modules.adminItemsLocations.items.colItemGrp2', {}, 'VareGrp. 2'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('itemGrp3', 'itemGrp3', t('manager.modules.adminItemsLocations.items.colItemGrp3', {}, 'VareGrp. 3'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('freeTxt1', 'freeTxt1', t('manager.modules.adminItemsLocations.items.freeText', { '0': 1 }, 'Fritekst 1'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('freeTxt2', 'freeTxt2', t('manager.modules.adminItemsLocations.items.freeText', { '0': 2 }, 'Fritekst 2'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('freeTxt3', 'freeTxt3', t('manager.modules.adminItemsLocations.items.freeText', { '0': 3 }, 'Fritekst 3'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('freeTxt4', 'freeTxt4', t('manager.modules.adminItemsLocations.items.freeText', { '0': 4 }, 'Fritekst 4'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('freeTxt5', 'freeTxt5', t('manager.modules.adminItemsLocations.items.freeText', { '0': 5 }, 'Fritekst 5'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('freeTxt6', 'freeTxt6', t('manager.modules.adminItemsLocations.items.freeText', { '0': 6 }, 'Fritekst 6'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('freeTxt7', 'freeTxt7', t('manager.modules.adminItemsLocations.items.freeText', { '0': 7 }, 'Fritekst 7'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('freeTxt8', 'freeTxt8', t('manager.modules.adminItemsLocations.items.freeText', { '0': 8 }, 'Fritekst 8'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('freeTxt9', 'freeTxt9', t('manager.modules.adminItemsLocations.items.freeText', { '0': 9 }, 'Fritekst 9'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('freeTxt10', 'freeTxt10', t('manager.modules.adminItemsLocations.items.freeText', { '0': 10 }, 'Fritekst 10'), {
      minWidth: 120,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('itemPictureName', 'itemPictureName', t('manager.modules.adminItemsLocations.items.colPictureName', {}, 'Bildenavn'), {
      minWidth: 150,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('stdLocationName', 'stdLocationName', t('manager.modules.adminItemsLocations.items.colStdLocation', {}, 'Standard Lok.'), {
      minWidth: 150,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createNumberColumn('reportID', 'reportID', t('manager.modules.adminItemsLocations.items.colDefaultLabel', {}, 'Standard etikett'), {
      minWidth: 180,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value: any, item: ItemsPagedDto) => value ? `${value} - ${item.reportName || ''}` : '-',
      editable: true,
      editType: 'select',
      editOptions: reportOptions
    }),

    columnFactory.createTextColumn('stdBinCode', 'stdBinCode', t('manager.modules.adminItemsLocations.items.colStdBinCode', {}, 'Fast Plukklokasjon'), {
      minWidth: 150,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createBooleanColumn('useSharedBinYesNo', 'useSharedBinYesNo', t('manager.modules.adminItemsLocations.items.colUseSharedBin', {}, 'Kan brukes på felles lok.'), {
      minWidth: 180,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createBooleanColumn('useMaxReplenishQtyYesNo', 'useMaxReplenishQtyYesNo', t('manager.modules.adminItemsLocations.items.colUseMaxReplenish', {}, 'Bruk maks påfylling'), {
      minWidth: 160,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createNumberColumn('maxReplenishQty', 'maxReplenishQty', t('manager.modules.adminItemsLocations.items.colMaxReplenishQty', {}, 'Maks påfyllingsantall'), {
      minWidth: 160,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('appFreeField1', 'appFreeField1', t('manager.modules.adminItemsLocations.items.appFreeText', { '0': 1 }, 'App Fritekst 1'), {
      minWidth: 150,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('appFreeField2', 'appFreeField2', t('manager.modules.adminItemsLocations.items.appFreeText', { '0': 2 }, 'App Fritekst 2'), {
      minWidth: 150,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('internalFree1', 'internalFree1', t('manager.modules.adminItemsLocations.items.internalFreeText', { '0': 1 }, 'Intern Fritekst 1'), {
      minWidth: 150,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('internalFree2', 'internalFree2', t('manager.modules.adminItemsLocations.items.internalFreeText', { '0': 2 }, 'Intern Fritekst 2'), {
      minWidth: 150,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('internalFree3', 'internalFree3', t('manager.modules.adminItemsLocations.items.internalFreeText', { '0': 3 }, 'Intern Fritekst 3'), {
      minWidth: 150,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('internalFree4', 'internalFree4', t('manager.modules.adminItemsLocations.items.internalFreeText', { '0': 4 }, 'Intern Fritekst 4'), {
      minWidth: 150,
      sortable: true,
      filterType: 'text',
      editable: true
    }),

    columnFactory.createTextColumn('updatedBy', 'updatedBy', t('manager.modules.adminItemsLocations.items.colUpdatedBy', {}, 'Oppdatert av'), {
      minWidth: 130,
      sortable: true,
      filterType: 'text'
    }),

    columnFactory.createDateColumn('lastUpdate', 'lastUpdate', t('manager.modules.adminItemsLocations.items.colLastUpdate', {}, 'Sist oppdatert'), {
      minWidth: 160,
      sortable: true,
      filterType: 'text'
    }),

    columnFactory.createTextColumn('createdBy', 'createdBy', t('manager.modules.adminItemsLocations.items.colCreatedBy', {}, 'Opprettet av'), {
      minWidth: 130,
      sortable: true,
      filterType: 'text'
    }),

    columnFactory.createDateColumn('created', 'created', t('manager.modules.adminItemsLocations.items.colCreated', {}, 'Opprettet'), {
      minWidth: 160,
      sortable: true,
      filterType: 'date'
    })
  ], [masterData, binGrpOptions, pickRuleOptions, sortingBoxOptions, movementTypeOptions, reportOptions, itemTypeOptions, itemBinSizeGroupOptions, t]);

  // Table actions for toolbar
  const tableActions: TableAction<ItemsPagedDto>[] = useMemo(() => [
    {
      id: 'print-labels',
      label: t('manager.modules.adminItemsLocations.items.actionPrintLabels', { '0': selectedItemNos.length }, `Skriv ut etiketter (${selectedItemNos.length})`),
      icon: Printer,
      onClick: () => setShowPrintModal(true),
      disabled: (_data: ItemsPagedDto[], _selected?: ItemsPagedDto[]) => selectedItemNos.length === 0,
      variant: 'secondary'
    },
    {
      id: 'bulk-edit',
      label: t('manager.modules.adminItemsLocations.items.actionMaintenance', { '0': selectedItemNos.length }, `Vedlikehold (${selectedItemNos.length})`),
      icon: Edit,
      onClick: () => handleBulkEdit(),
      disabled: (_data: ItemsPagedDto[], _selected?: ItemsPagedDto[]) => selectedItemNos.length === 0,
      variant: 'primary'
    },
    {
      id: 'copy-properties',
      label: t('manager.modules.adminItemsLocations.items.actionCopyProperties', { '0': selectedItemNos.length }, `Kopier egenskaper (${selectedItemNos.length})`),
      icon: Copy,
      onClick: () => setShowCopyPropsModal(true),
      disabled: (_data: ItemsPagedDto[], _selected?: ItemsPagedDto[]) => selectedItemNos.length === 0,
      variant: 'secondary'
    },
    {
      id: 'refresh',
      label: t('common.buttons.refresh', {}, 'Oppdater'),
      icon: RefreshCw,
      onClick: () => refreshData(),
      variant: 'secondary'
    }
  ], [selectedItemNos.length, handleBulkEdit, refreshData, t]);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      {saveError && (
        <div className="mx-4 mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <span className="text-sm text-red-700">{saveError}</span>
          <button onClick={() => setSaveError(null)} className="text-red-400 hover:text-red-600 ml-4">✕</button>
        </div>
      )}
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={serverItems}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: false,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'items-table',
            initialColumnFilters: initialItemNoFilter ? { itemNo: initialItemNoFilter } : undefined,
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'current-page'
            }
          }}
          serverPagination={{
            enabled: true,
            totalRecords: totalRecords,
            onFetchData: handleServerFetch
          }}
          selectedRows={selectedItemNos}
          onSelectionChange={handleSelectionChange}
          getRowId={getRowId}
          tableActions={tableActions}
          inlineEditConfig={inlineEditConfig}
          enableDropdownFilters={true}
          enableEmptyFilters={true}
          dropdownOptionsData={dropdownOptionsData.length > 0 ? dropdownOptionsData : undefined}
          loading={isLoading}
          error={error || undefined}
        />
      </div>

      {/* Bulk Edit Modal */}
      {showBulkEditModal && selectedItemNos.length > 0 && (
        <ItemsBulkUpdateModal
          isOpen={showBulkEditModal}
          onClose={() => setShowBulkEditModal(false)}
          selectedItemNos={selectedItemNos}
          companyNo={companyNo}
          onUpdateSuccess={handleUpdateSuccess}
          onDeleteSuccess={handleDeleteSuccess}
          selectedItemData={serverItems.filter(i => selectedItemNos.includes(i.itemNo))}
        />
      )}

      {/* Copy Properties Modal */}
      {showCopyPropsModal && selectedItemNos.length > 0 && (
        <ItemsCopyPropertiesModal
          isOpen={showCopyPropsModal}
          onClose={() => setShowCopyPropsModal(false)}
          selectedItemNos={selectedItemNos}
          selectedItemData={serverItems.filter(i => selectedItemNos.includes(i.itemNo))}
          companyNo={companyNo}
          onSuccess={handleCopyPropsSuccess}
        />
      )}

      {/* Print Labels Modal */}
      {showPrintModal && selectedItemNos.length > 0 && (
        <LabelPrintModal
          isOpen={showPrintModal}
          onClose={() => setShowPrintModal(false)}
          printType="item"
          items={serverItems
            .filter(i => selectedItemNos.includes(i.itemNo))
            .map(i => ({
              id: i.itemNo,
              displayName: i.description1 ? `${i.itemNo} - ${i.description1}` : i.itemNo,
              itemNo: i.itemNo,
              itemDescription: i.description1,
            }))}
        />
      )}
    </div>
  );
};
